import addDays from 'date-fns/addDays'
import differenceInDays from 'date-fns/differenceInDays'
import endOfMonth from 'date-fns/endOfMonth'
import startOfMonth from 'date-fns/startOfMonth'
import subDays from 'date-fns/subDays'
import { action, comparer, computed, makeObservable } from 'mobx'

import { formatDate, parseISODate } from '@avantstay/backoffice-core'

import { FilterStore } from '@new/__global/components/FiltersFramework'
import { HotelFilterIds } from '@new/domains/hotelTapeChart/config/filtersConfig'

import { computedRouteParam } from '@stores/computedRouteParam'

import { AvailableDatesRange, BookingsColorMode } from '../__types'
import {
  DEFAULT_VIEW_FROM_DATE,
  fastTapeChartPaths,
  ISO_DATE_FORMAT,
  TIME_FRAME_DAYS,
} from '../config/contants'
import { fastTapeChartFiltersConfig, HomeFilterIds } from '../config/filtersConfig'
import { CustomFilterIds, ViewStateFilterIds } from '../internal'

export class FastTapeChartFiltersStore extends FilterStore {
  constructor() {
    super(fastTapeChartFiltersConfig, fastTapeChartPaths)

    makeObservable(this, {
      viewFrom: computed({ equals: comparer.structural }),
      startDate: computed,
      endDate: computed,
      availableDates: computed,
      nightlyRates: computed,
      reservationStatus: computed,
      paymentStatus: computed,
      depositOnHold: computed,
      bookingIssues: computed,
      colorMode: computed,
      arrivalsMode: computed,
      unassignedBookingsView: computed({ equals: comparer.structural }),
      hotelId: computed,
      roomTypeId: computed,
      propertyId: computed,
      bookingId: computed,
      blockId: computed,
      checkInExistsBetween: computed,
      setViewFrom: action.bound,
      setAvailableDates: action.bound,
      setColorMode: action.bound,
      setAddressFilter: action.bound,
      setCheckInExistsBetween: action.bound,
      setBookingId: action.bound,
      setBlockId: action.bound,
    })
  }

  get viewFrom() {
    const viewFrom = this.computedRouteParams[ViewStateFilterIds.ViewFrom].get()

    return viewFrom ? parseISODate(viewFrom) : DEFAULT_VIEW_FROM_DATE
  }

  get startDate(): Date {
    const previous7days =
      differenceInDays(this.viewFrom, endOfMonth(this.viewFrom)) === 0
        ? subDays(this.viewFrom, 1)
        : this.viewFrom

    // we can't start our calendar from last day of month
    // because header will not fit and break the grid
    // we also can't use overflow: hidden as it break position: sticky
    // that's why in such case we add one more day so the header will have enough space
    return subDays(previous7days, 8)
  }

  get endDate() {
    // we can't end our calendar on the last first of month
    // because header will not fit and grid will have empty space at the end
    // we also can't use overflow: hidden as it break position: sticky
    // that's why in such case we add one more day so the header will have enough space
    const endDate = addDays(this.viewFrom, TIME_FRAME_DAYS)
    return differenceInDays(endDate, startOfMonth(endDate)) === 0 ? addDays(endDate, 1) : endDate
  }

  get availableDates() {
    const dateRange: AvailableDatesRange | undefined =
      this.computedRouteParams[CustomFilterIds.AvailableDates].get()
    if (!dateRange) {
      return undefined
    }
    const start = parseISODate(dateRange.checkIn)
    const end = parseISODate(dateRange.checkOut)
    return { start, end }
  }

  get nightlyRates() {
    return this.computedRouteParams[ViewStateFilterIds.NightlyRates].get()
  }

  get reservationStatus() {
    return this.computedRouteParams[ViewStateFilterIds.ReservationStatus].get()
  }

  get paymentStatus() {
    return this.computedRouteParams[ViewStateFilterIds.PaymentStatus].get()
  }

  get depositOnHold() {
    return this.computedRouteParams[ViewStateFilterIds.DepositsOnHold].get() === 'true'
  }

  get bookingIssues() {
    return this.computedRouteParams[ViewStateFilterIds.BookingIssues].get()
  }

  get colorMode() {
    return this.computedRouteParams[ViewStateFilterIds.ColorBy].get()
  }

  get arrivalsMode() {
    return this.computedRouteParams[ViewStateFilterIds.ArrivalsMode].get()
  }

  // Shared components access this property via the base store type.
  // The bookings tape chart doesn't support unassigned bookings,
  // so we return undefined here to satisfy the interface.
  get unassignedBookingsView() {
    return undefined
  }

  get hotelId() {
    return this.computedRouteParams[HotelFilterIds.HotelId]?.get()
  }

  get roomTypeId() {
    return this.computedRouteParams[HotelFilterIds.RoomType]?.get()
  }

  private propertyIdParam = computedRouteParam<string | undefined>('property-id', {
    patterns: fastTapeChartPaths,
  })

  private bookingIdParam = computedRouteParam<string | undefined>('booking-id', {
    patterns: fastTapeChartPaths,
  })

  private blockIdParam = computedRouteParam<string | undefined>('block-id', {
    patterns: fastTapeChartPaths,
  })

  get propertyId() {
    return this.propertyIdParam.get()
  }

  get bookingId() {
    return this.bookingIdParam.get()
  }

  get blockId() {
    return this.blockIdParam.get()
  }

  get checkInExistsBetween() {
    const dateRange: AvailableDatesRange | undefined =
      this.computedRouteParams[CustomFilterIds.CheckInExistsBetween]?.get()
    if (!dateRange) {
      return undefined
    }

    const start = parseISODate(dateRange.checkIn)
    const end = parseISODate(dateRange.checkOut)
    return { start, end }
  }

  setViewFrom(date: Date) {
    this.computedRouteParams[ViewStateFilterIds.ViewFrom].replace(formatDate(date, ISO_DATE_FORMAT))
  }

  setAvailableDates(period?: { fromDate?: string; toDate?: string }) {
    if (period) {
      this.computedRouteParams[CustomFilterIds.AvailableDates].replace({
        checkIn: period?.fromDate,
        checkOut: period?.toDate,
        ignoreBlocks: true,
      })
    } else {
      this.computedRouteParams[CustomFilterIds.AvailableDates].replace(undefined)
    }
  }

  setColorMode(mode: BookingsColorMode) {
    this.computedRouteParams[ViewStateFilterIds.ColorBy].push(mode)
  }

  setAddressFilter(value?: string) {
    this.computedRouteParams[HomeFilterIds.AddressRadius].push(value)
  }

  setCheckInExistsBetween(value?: AvailableDatesRange) {
    this.computedRouteParams[CustomFilterIds.CheckInExistsBetween].replace(value)
  }

  setBookingId(bookingId?: string) {
    computedRouteParam<string | undefined>('booking-id', {
      patterns: fastTapeChartPaths,
    }).push(bookingId)
  }

  setBlockId(blockId?: string) {
    computedRouteParam<string | undefined>('block-id', {
      patterns: fastTapeChartPaths,
    }).push(blockId)
  }
}
